import React, { useState, useEffect } from "react";
import { FiEdit, FiTrash2, FiPlusCircle, FiX } from "react-icons/fi";
import { toast } from "react-toastify";
import "../ContentManagement.css";
import AxiosWeb from "../../../utils/axiosWeb";

const Sections = ({ setSectionsData }) => {
  const [sections, setSections] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [sectionToDelete, setSectionToDelete] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);
  const [newSection, setNewSection] = useState(getEmptySection());
  const [isLoading, setIsLoading] = useState(false);

  function getEmptySection() {
    return {
      title: "",
      description: "",
      displaytype: "HORIZONTAL",
      type: "SOLUTIONS",
      displayOrder: sections.length
        ? Math.max(...sections.map((s) => s.displayOrder)) + 1
        : 1,
      file: null,
      fileType: "",
      content: "",
    };
  }

  const updateParent = (updated) => {
    setSections(updated);
    if (setSectionsData) setSectionsData(updated);
  };

  const fetchSections = async () => {
    try {
      setIsLoading(true);
      const res = await AxiosWeb.get(
        `${process.env.REACT_APP_BASE_URL}/section/getAllSections`
      );
      setSections(res?.data || []);
      updateParent(res?.data || []);
    } catch (error) {
      toast.error(error?.response?.data?.message || error.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSections();
  }, []);

  const handleAdd = () => {
    setNewSection(getEmptySection());
    setEditingIndex(null);
    setShowModal(true);
  };

  const handleEdit = (index) => {
    const section = sections[index];
    const fileType = section.contentType?.startsWith("image")
      ? "image"
      : section.contentType?.startsWith("video")
      ? "video"
      : "";
    setEditingIndex(index);
    setNewSection({
      ...section,
      displaytype: section.displayType || section.displaytype || "HORIZONTAL",
      file: null,
      fileType,
      content: section.contentUrl || "",
    });
    setShowModal(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setNewSection((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    if (!e.target.files || !e.target.files[0]) return;
    const file = e.target.files[0];
    console.log(file);
    const fileType = file.type.startsWith("image")
      ? "image"
      : file.type.startsWith("video")
      ? "video"
      : "";

    const reader = new FileReader();
    reader.onload = (event) => {
      setNewSection((prev) => ({
        ...prev,
        file,
        fileType,
        content: event.target.result,
      }));
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!newSection.title.trim() || !newSection.description.trim()) {
      toast.error("Title and Description are required");
      return;
    }

    try {
      setIsLoading(true);
      const formData = new FormData();
      formData.append("title", newSection.title);
      formData.append("description", newSection.description);
      formData.append("type", newSection.type);
      formData.append("displayOrder", newSection.displayOrder);
      formData.append("displayType", newSection.displaytype);
      if (newSection.file) formData.append("content", newSection.file);

      let res;
      if (editingIndex !== null) {
        // Update section
        const sectionToUpdate = sections[editingIndex];
        res = await AxiosWeb.put(
          `${process.env.REACT_APP_BASE_URL}/section/updateSection?id=${sectionToUpdate.id}`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );
        const updated = [...sections];
        updated[editingIndex] = res.data;
        updateParent(updated);
        toast.success("Section updated successfully!");
      } else {
        // Create section
        res = await AxiosWeb.post(
          `${process.env.REACT_APP_BASE_URL}/section/createSection`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );
        console.log(formData);
        updateParent([...sections, res.data]);
        toast.success("Section created successfully!");
      }

      setShowModal(false);
      setNewSection(getEmptySection());
      setEditingIndex(null);
    } catch (error) {
      toast.error(error?.response?.data?.message || error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const confirmDelete = (index) => {
    setSectionToDelete(index);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (sectionToDelete === null) return;
    try {
      setIsLoading(true);
      const section = sections[sectionToDelete];
      await AxiosWeb.delete(
        `${process.env.REACT_APP_BASE_URL}/section/deleteSection?id=${section.id}`
      );
      updateParent(sections.filter((_, i) => i !== sectionToDelete));
      toast.success("Section deleted successfully!");
      setShowDeleteModal(false);
      setSectionToDelete(null);
    } catch (error) {
      toast.error(error?.response?.data?.message || error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: 16,
        }}
      >
        <button
          onClick={handleAdd}
          className="add-new-btn"
          disabled={isLoading}
        >
          <FiPlusCircle size={18} /> Add New Section
        </button>
      </div>

      {sections
        .sort((a, b) => a.displayOrder - b.displayOrder)
        .map((section, index) => (
          <div key={section.id} className="content-card">
            <div className="content-card-header">
              <h3>{section.title}</h3>
              <div>
                <button
                  onClick={() => handleEdit(index)}
                  className="icon-btn edit-btn"
                  disabled={isLoading}
                >
                  <FiEdit size={18} />
                </button>
                <button
                  onClick={() => confirmDelete(index)}
                  className="icon-btn delete-btn"
                  disabled={isLoading}
                >
                  <FiTrash2 size={18} />
                </button>
              </div>
            </div>
            <div className="content-details-expanded">
              <p>
                <strong>Description:</strong> {section.description}
              </p>
              <p>
                <strong>Type:</strong> {section.type}
              </p>
              <p>
                <strong>Display Type:</strong> {section.displaytype}
              </p>
              <p>
                <strong>Display Order:</strong> {section.displayOrder}
              </p>

              {section.contentType?.startsWith("image") &&
                section.contentUrl && (
                  <img
                    src={section.contentUrl}
                    alt={section.title}
                    className="content-image"
                  />
                )}
              {section.contentType?.startsWith("video") &&
                section.contentUrl && (
                  <video
                    src={section.contentUrl}
                    controls
                    className="content-video"
                  />
                )}
            </div>
          </div>
        ))}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{editingIndex !== null ? "Edit Section" : "Add Section"}</h3>
              <button
                className="modal-close-btn"
                onClick={() => setShowModal(false)}
                disabled={isLoading}
              >
                <FiX size={24} />
              </button>
            </div>
            <form onSubmit={handleSave} className="edit-form">
              <label>
                Title *
                <input
                  type="text"
                  name="title"
                  value={newSection.title}
                  onChange={handleChange}
                  disabled={isLoading}
                />
              </label>
              <label>
                Description *
                <textarea
                  name="description"
                  value={newSection.description}
                  onChange={handleChange}
                  disabled={isLoading}
                />
              </label>
              <label>
                Type
                <select
                  name="type"
                  value={newSection.type}
                  onChange={handleChange}
                  disabled={isLoading}
                >
                  <option value="SOLUTIONS">SOLUTIONS</option>
                  <option value="HERO">HERO</option>
                  <option value="TEXT">TEXT</option>
                </select>
              </label>
              <label>
                DisplayType
                <select
                  name="displaytype"
                  value={newSection.displaytype}
                  onChange={handleChange}
                  disabled={isLoading}
                >
                  <option value="HORIZONTAL">HORIZONTAL</option>
                  <option value="VERTICAL">VERTICAL</option>
                </select>
              </label>
              <label>
                Display Order
                <input
                  type="number"
                  name="displayOrder"
                  value={newSection.displayOrder}
                  onChange={handleChange}
                  disabled={isLoading}
                />
              </label>
              <label>
                Upload File
                <input
                  type="file"
                  accept="image/*,video/*"
                  onChange={handleFileChange}
                  disabled={isLoading}
                />
              </label>
              {newSection.content && newSection.fileType === "image" && (
                <img
                  src={newSection.content}
                  alt="Preview"
                  className="content-image-preview"
                />
              )}
              {newSection.content && newSection.fileType === "video" && (
                <video
                  src={newSection.content}
                  controls
                  className="content-video-preview"
                />
              )}
              <div className="form-actions">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => setShowModal(false)}
                  disabled={isLoading}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-save" disabled={isLoading}>
                  {isLoading ? "Saving..." : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div
            className="modal-content"
            style={{ maxWidth: "400px", textAlign: "center" }}
          >
            <h3 style={{ color: "#2a4e85", marginBottom: "15px" }}>
              Confirm Delete
            </h3>
            <p style={{ color: "#555", marginBottom: "25px" }}>
              Are you sure you want to delete this section?
            </p>
            <div className="form-actions" style={{ justifyContent: "center" }}>
              <button
                type="button"
                className="btn-cancel"
                onClick={() => setShowDeleteModal(false)}
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-save"
                style={{ backgroundColor: "#dc3545" }}
                onClick={handleDelete}
                disabled={isLoading}
              >
                {isLoading ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Sections;
